"use client";

import type { ChangeEvent } from "react";

type LogoUploadCardProps = {
  logoPosition: {
    x: number;
    y: number;
  };
  logoSrc: string;
  logoSize: number;
  onFileSelect: (event: ChangeEvent<HTMLInputElement>) => void;
  onLogoPositionChange: (position: { x: number; y: number }) => void;
  onLogoSizeChange: (value: number) => void;
  onReset: () => void;
  onUrlChange: (value: string) => void;
  uploadLabel: string;
  useDefaultLabel: string;
};

export default function LogoUploadCard({
  logoPosition,
  logoSrc,
  logoSize,
  onFileSelect,
  onLogoPositionChange,
  onLogoSizeChange,
  onReset,
  onUrlChange,
  uploadLabel,
  useDefaultLabel,
}: LogoUploadCardProps) {
  return (
    <div className="grid gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
      <div className="grid gap-2 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start">
        <div className="rounded-xl border border-slate-200 bg-[#fcfaf5] p-2.5">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-900">Cover logo</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <label className="inline-flex cursor-pointer items-center justify-center rounded-full bg-[#b59453] px-3 py-1.5 text-xs font-semibold text-white transition-all duration-200 hover:bg-[#a78648]">
                {uploadLabel}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/jpg"
                  className="hidden"
                  onChange={onFileSelect}
                />
              </label>
              <button
                type="button"
                onClick={onReset}
                className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 transition-all duration-200 hover:text-slate-700"
              >
                {useDefaultLabel}
              </button>
            </div>
          </div>
        </div>

        <label className="grid gap-1.5 text-xs font-semibold text-slate-900">
          Current logo URL
          <input
            value={logoSrc}
            onChange={(event) => onUrlChange(event.target.value)}
            placeholder="https://example.com/logo.png"
            className="rounded-xl border border-slate-200 bg-[#fcfaf5] px-3 py-2 text-xs text-black outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#b59453] focus:ring-2 focus:ring-[#e8d8bb]"
          />
        </label>

        <label className="grid gap-1.5 text-xs font-semibold text-slate-900 lg:col-span-2">
          Logo size
          <div className="grid gap-2 rounded-xl border border-slate-200 bg-[#fcfaf5] p-2.5 sm:grid-cols-[1fr_4.75rem] sm:items-center">
            <input
              type="range"
              min={36}
              max={140}
              value={logoSize}
              onChange={(event) => onLogoSizeChange(Number(event.target.value))}
              className="accent-[#b59453]"
            />
            <input
              type="number"
              min={36}
              max={140}
              value={logoSize}
              onChange={(event) => onLogoSizeChange(Number(event.target.value))}
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-black outline-none focus:border-[#b59453] focus:ring-2 focus:ring-[#e8d8bb]"
            />
          </div>
        </label>

        <div className="grid gap-2 rounded-xl border border-slate-200 bg-[#fcfaf5] p-2.5 lg:col-span-2">
          <p className="text-xs font-semibold text-slate-900">Logo position</p>
          <label className="grid gap-2 text-xs font-semibold text-slate-700 sm:grid-cols-[2rem_1fr_4rem] sm:items-center">
            X
            <input
              type="range"
              min={0}
              max={100}
              value={logoPosition.x}
              onChange={(event) => onLogoPositionChange({ ...logoPosition, x: Number(event.target.value) })}
              className="accent-[#b59453]"
            />
            <input
              type="number"
              min={0}
              max={100}
              value={Math.round(logoPosition.x)}
              onChange={(event) => onLogoPositionChange({ ...logoPosition, x: Number(event.target.value) })}
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-black outline-none focus:border-[#b59453] focus:ring-2 focus:ring-[#e8d8bb]"
            />
          </label>
          <label className="grid gap-2 text-xs font-semibold text-slate-700 sm:grid-cols-[2rem_1fr_4rem] sm:items-center">
            Y
            <input
              type="range"
              min={0}
              max={100}
              value={logoPosition.y}
              onChange={(event) => onLogoPositionChange({ ...logoPosition, y: Number(event.target.value) })}
              className="accent-[#b59453]"
            />
            <input
              type="number"
              min={0}
              max={100}
              value={Math.round(logoPosition.y)}
              onChange={(event) => onLogoPositionChange({ ...logoPosition, y: Number(event.target.value) })}
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-black outline-none focus:border-[#b59453] focus:ring-2 focus:ring-[#e8d8bb]"
            />
          </label>
        </div>
      </div>
    </div>
  );
}
